import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  Gift,
  Loader2,
  ShieldCheck,
  TicketCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const normalizeCode = (value) => String(value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9-]/g, '')
  .slice(0, 64);

export default function PromoCodePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { checkAuth } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  const canSubmit = useMemo(() => code.trim().length >= 3 && !submitting, [code, submitting]);

  const submit = async (event) => {
    event.preventDefault();
    const promoCode = normalizeCode(code).trim();
    if (!promoCode) {
      toast.error('Enter your promo code');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/api/promo/redeem', { code: promoCode });
      const payload = response.data || {};
      setResult(payload);
      await checkAuth?.();
      toast.success(payload.benefit || 'Promo reward claimed');
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Could not redeem this promo code';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="player-app min-h-screen pb-10"
      style={{ color: '#FFFFFF' }}
      data-testid="promo-code-page"
    >
      <header
        className="sticky top-0 z-30 flex items-center gap-3 px-4 py-4 sm:px-6"
        style={{
          background: 'rgba(0,0,0,0.92)',
          borderBottom: '1px solid rgba(212,175,55,0.30)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(explorePath)}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full transition active:scale-95"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
          aria-label="Back to rewards"
          data-testid="promo-code-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="display flex items-center gap-2 text-xl font-extrabold sm:text-2xl">
            <TicketCheck className="h-6 w-6 text-amber-300" />
            Promo Code
          </h1>
          <p className="text-xs text-white/65">Redeem an approved one-time reward.</p>
        </div>
      </header>

      <main className="mx-auto flex min-h-[calc(100vh-82px)] w-full max-w-xl items-start px-4 py-8 sm:px-6 sm:py-12">
        <section
          className="w-full overflow-hidden rounded-[24px] border"
          style={{
            background: 'linear-gradient(155deg, rgba(14,37,51,0.98), rgba(7,18,29,0.98))',
            borderColor: 'rgba(212,175,55,0.32)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
        >
          <div className="border-b border-white/10 px-5 py-6 sm:px-7">
            <span
              className="grid h-14 w-14 place-items-center rounded-[18px]"
              style={{
                background: 'linear-gradient(135deg, #8A5A00, #D4AF37)',
                color: '#FFF7CC',
                boxShadow: '0 12px 28px rgba(212,175,55,0.22)',
              }}
              aria-hidden="true"
            >
              <Gift className="h-7 w-7" strokeWidth={2.4} />
            </span>
            <p className="mt-5 text-[11px] font-black uppercase tracking-[0.18em] text-amber-300">Task Reward</p>
            <h2 className="display mt-1 text-2xl font-black text-white sm:text-3xl">Enter your code</h2>
            <p className="mt-2 max-w-md text-sm leading-6 text-white/65">
              Use the one-time code shared by your agent after your task is approved.
            </p>
          </div>

          {result ? (
            <div className="px-5 py-7 text-center sm:px-7" data-testid="promo-code-success">
              <span
                className="mx-auto grid h-16 w-16 place-items-center rounded-full"
                style={{ background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.45)', color: '#86EFAC' }}
              >
                <CheckCircle2 className="h-9 w-9" strokeWidth={2.5} />
              </span>
              <h3 className="display mt-4 text-2xl font-black text-white">Reward claimed</h3>
              <p className="mt-2 text-base font-bold text-emerald-300">
                {result.benefit || result.message || 'Your reward was added successfully.'}
              </p>
              {result.promo?.title && (
                <p className="mt-1 text-sm text-white/55">{result.promo.title}</p>
              )}
              <button
                type="button"
                onClick={() => navigate(explorePath)}
                className="mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl font-black text-black transition hover:brightness-105 active:scale-[0.99]"
                style={{ background: 'linear-gradient(135deg, #F6C945, #D4AF37)' }}
                data-testid="promo-code-success-back"
              >
                Back to Rewards <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="px-5 py-6 sm:px-7" data-testid="promo-code-form">
              <label htmlFor="player-promo-code" className="block text-xs font-black uppercase tracking-[0.14em] text-white/60">
                Promo code
              </label>
              <input
                id="player-promo-code"
                value={code}
                onChange={(event) => setCode(normalizeCode(event.target.value))}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck="false"
                inputMode="text"
                placeholder="ENTER-CODE"
                className="mt-2 h-14 w-full rounded-2xl px-4 font-mono text-lg font-black tracking-[0.12em] text-white outline-none transition placeholder:tracking-normal placeholder:text-white/28 focus:ring-4"
                style={{
                  background: 'rgba(0,0,0,0.28)',
                  border: '1.5px solid rgba(212,175,55,0.42)',
                  '--tw-ring-color': 'rgba(212,175,55,0.14)',
                }}
                data-testid="promo-code-input"
              />

              <div className="mt-3 flex items-start gap-2 text-xs leading-5 text-white/55">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                <span>Admin limits and one-time-use rules apply automatically.</span>
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl font-black text-black transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
                style={{ background: 'linear-gradient(135deg, #F6C945, #D4AF37)' }}
                data-testid="promo-code-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Checking code...</>
                ) : (
                  <>Claim Reward <ArrowRight className="h-5 w-5" /></>
                )}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
